// Pure row logic for the endpoint inventory — id normalization, the
// EndpointMetadataInfo flattening, and the filter/sort helpers. Split
// from components/endpoints/inventoryTable.jsx (which re-exports all of
// this alongside the JSX cell renderer) so the logic is importable in
// node tests without dragging the component import chain (AgentIcon →
// Dimension → dimAdapters → stores) into a DOM-less environment.

import { parseServerTimestamp } from "./traceUtils.js";
import { agentDisplayLabel } from "./agentIdentity.js";
import { compareVersions } from "./semver.js";

const PLATFORM_LABEL = {
    PLATFORM_WINDOWS: "Windows",
    PLATFORM_MACOS:   "macOS",
    PLATFORM_LINUX:   "Linux",
    PLATFORM_UNKNOWN: "Unknown",
};

export const INVENTORY_SEARCHABLE_FIELDS = [
    "hostname", "serial", "registeredUser", "platform",
    "agentVersion", "watchdogVersion",
    "manufacturer", "model", "processorModel",
    "osDisplayVersion", "osEdition", "installedAiAgents",
    "agentExecutableSha256", "watchdogExecutableSha256",
];

// The REST metadata endpoint id arrives as the 16-byte UUID serialized
// as base64 (proto `bytes` JSON encoding, e.g. "bhEduJyJRTuf/gO1Qn2lbw=="),
// but the analytics API's EventEndpointId field expects a canonical
// hyphenated UUID string — feeding the base64 form into a scope step
// fails with "not parseable as UUID". Convert once here so the id works
// both as a scope id (recent-trace preview + Open-in-Explore deeplink)
// and as a readable display value.
export function endpointIdToUuid(raw) {
    if (typeof raw !== "string" || raw.length === 0) return raw || "";
    // Already canonical — pass through.
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(raw)) return raw;
    try {
        const bin = atob(raw);
        if (bin.length !== 16) return raw;
        let hex = "";
        for (let i = 0; i < 16; i++) hex += bin.charCodeAt(i).toString(16).padStart(2, "0");
        return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    } catch {
        return raw;
    }
}

// The inverse of `endpointIdToUuid`, for the other direction of the same
// mismatch: the analytics API hands back hyphenated UUIDs (e.g. a signal tag's
// endpoint subject_id), but a REST/proto `bytes` field wants the 16 bytes
// base64-encoded. Needed by the endpoint config-state read, which starts from an
// analytics-sourced endpoint id and calls a proto RPC with it.
//
// This dashboard otherwise carries ids as base64 end-to-end and deliberately has
// no UUID-form helper, so this is an EXCEPTION, not the pattern to copy: it exists
// because the root AGENTS.md requires Origin UUIDs to be `bytes` on the wire (so
// the RPC cannot take the UUID form) while analytics cannot emit base64. Both
// sanctioned conversions live in this module and are named in
// `dashboard/AGENTS.md` → IDs on the wire; a new call site should reach for
// neither.
export function uuidToEndpointId(raw) {
    if (typeof raw !== "string" || raw.length === 0) return raw || "";
    const hex = raw.replace(/-/g, "");
    if (!/^[0-9a-f]{32}$/i.test(hex)) return raw; // already base64, or not an id
    let bin = "";
    for (let i = 0; i < 32; i += 2) bin += String.fromCharCode(parseInt(hex.slice(i, i + 2), 16));
    return btoa(bin);
}

// Display name for an installed-agent entry: the specific tool name
// ("Ollama", "Claude Code") when the agent reported one, falling back to
// the provider tag, then to the legacy tool_type enum for old reports.
function agentDisplayName(a) {
    return (
        a.toolName ?? a.tool_name ??
        a.toolProvider ?? a.tool_provider ??
        a.toolType ?? a.tool_type ?? ""
    );
}

// Derive the "OS version" / "OS edition" table cells from the
// platform-specific OsVersion map the agent reports. The keys differ by
// platform (see shared-protos endpoint_metadata.proto OsVersion):
//   - Windows: major, minor, build, revision, edition, display_version, product_name
//   - macOS:   major, minor, build_identifier, codename
// Both columns were originally wired only to the Windows keys
// (display_version / edition), so macOS rows rendered "—" for both even
// though the agent does report the version (major.minor + codename via
// sw_vers). macOS has no Windows-style release tag and no SKU/"edition",
// so surface the product version (with the codename when known) and leave
// edition blank — there is genuinely no macOS edition to show.
export function deriveOsVersionCells(platform, osVersionMap) {
    const v = osVersionMap ?? {};
    if (String(platform ?? "").toUpperCase().includes("MACOS")) {
        const major = String(v.major ?? "").trim();
        const minor = String(v.minor ?? "").trim();
        const codename = String(v.codename ?? "").trim();
        // The agent defaults a missing major/minor to "0"; treat a "0"
        // major as "unknown" rather than rendering a misleading "0.0".
        const product =
            major && major !== "0" ? (minor !== "" ? `${major}.${minor}` : major) : "";
        const hasCodename = codename !== "" && codename !== "Unknown";
        const osDisplayVersion =
            product && hasCodename
                ? `${product} (${codename})`
                : product || (hasCodename ? codename : "");
        return { osDisplayVersion, osEdition: "" };
    }
    // Windows (and any platform following the Windows key scheme): unchanged.
    return {
        osDisplayVersion: v.display_version ?? v.displayVersion ?? "",
        osEdition: v.edition ?? "",
    };
}

// Flatten the nested EndpointMetadataInfo for table use.
export function flattenInventoryRow(info) {
    const m = info?.metadata ?? {};
    const host = m.hostInfo  ?? m.host_info  ?? {};
    const hw   = m.hardwareInfo ?? m.hardware_info ?? {};
    const osv  = m.osVersion ?? m.os_version ?? {};
    const osVersionMap = osv.version ?? {};
    const installed = m.installedAiAgents ?? m.installed_ai_agents ?? [];
    const identity = info?.identityInfo ?? info?.identity_info ?? {};

    // Prefer the build-corrected Windows product name (e.g. "Windows 11 Pro") for the
    // default "Operating system" column so it distinguishes Windows 10 from 11 — the bare
    // platform label ("Windows") cannot. The agent emits "Unknown" when it can't resolve
    // the name; fall back to the platform label there, and on macOS/Linux or older agents
    // that don't report product_name at all.
    const osProductName = osVersionMap.product_name ?? osVersionMap.productName ?? "";
    const platformLabel = PLATFORM_LABEL[m.platform] ?? m.platform ?? "";
    const operatingSystem =
        osProductName && osProductName !== "Unknown" ? osProductName : platformLabel;

    // "OS version" / "OS edition" cells are platform-key-dependent — see
    // deriveOsVersionCells (macOS reports major.minor+codename, not the
    // Windows display_version/edition keys these columns originally read).
    const { osDisplayVersion, osEdition } = deriveOsVersionCells(m.platform, osVersionMap);

    const idRaw = info?.endpointId ?? info?.endpoint_id ?? "";
    const id = endpointIdToUuid(idRaw);
    const snapRaw = m.snapshotTime ?? m.snapshot_time ?? null;
    const snapshotTime = snapRaw ? new Date(parseServerTimestamp(snapRaw)) : null;

    const aiAgents = dedupeAiAgents(installed);
    const forkedFromEndpointIdRaw =
        identity.forkedFromEndpointId ?? identity.forked_from_endpoint_id ?? "";

    return {
        id,
        idRaw,
        hostname:        host.hostname ?? "",
        serial:          host.serialNumber ?? host.serial_number ?? "",
        deviceFingerprintV1Sha256:
            host.deviceFingerprintV1Sha256 ||
            host.device_fingerprint_v1_sha256 ||
            identity.deviceFingerprintV1Sha256 ||
            identity.device_fingerprint_v1_sha256 || "",
        identityCreationReason:
            identity.creationReason ?? identity.creation_reason ?? "",
        forkedFromEndpointIdRaw,
        forkedFromEndpointId: endpointIdToUuid(forkedFromEndpointIdRaw),
        registeredUser:  host.registeredUser ?? host.registered_user ?? "",
        platform:        operatingSystem,
        agentVersion:    m.agentVersion    ?? m.agent_version    ?? "",
        watchdogVersion: m.watchdogVersion ?? m.watchdog_version ?? "",
        agentExecutableSha256:    m.agentExecutableSha256    ?? m.agent_executable_sha256    ?? "",
        watchdogExecutableSha256: m.watchdogExecutableSha256 ?? m.watchdog_executable_sha256 ?? "",
        snapshotTime:    Number.isFinite(snapshotTime?.getTime?.()) ? snapshotTime : null,
        manufacturer:    hw.manufacturer ?? "",
        model:           hw.model ?? "",
        processorModel:  hw.processorModel ?? hw.processor_model ?? "",
        coreCount:       hw.coreCount ?? hw.core_count ?? null,
        systemMemoryMb:  hw.systemMemoryMb ?? hw.system_memory_mb ?? null,
        primaryDriveDiskSizeGb:  hw.primaryDriveDiskSizeGb  ?? hw.primary_drive_disk_size_gb  ?? null,
        primaryDriveFreeSpaceGb: hw.primaryDriveFreeSpaceGb ?? hw.primary_drive_free_space_gb ?? null,
        osDisplayVersion,
        osEdition,
        // Installed AI agents, collapsed to one row per tool (see
        // `dedupeAiAgents`). Exposed as a structured list (renderer colors the
        // version distinct from the name) and a flat string (search index,
        // OverflowTooltip text), both derived from a single dedupe pass so they
        // can't drift and the loop runs once per row. Display name prefers the
        // specific tool name ("Ollama", "Claude Code"); older reports fall back
        // to the provider tag, then the legacy `tool_type` enum.
        installedAiAgentsList: aiAgents,
        installedAiAgents: aiAgents
            .map((a) => (a.version ? `${a.name} ${a.version}` : a.name))
            .join(", "),
    };
}

// The analytics graph returns `operating_system` as its ClickHouse Enum8 label
// (`Windows` / `MacOs` / `Linux` / `Unknown`, per the endpoint_metadata_current
// schema), but the shared reshape keys `platform` off the proto enum name
// (`PLATFORM_*`). Normalize at the graph boundary so both the exact-match
// PLATFORM_LABEL lookup and deriveOsVersionCells see the same value the
// REST/proto path produces — otherwise `MacOs` misses PLATFORM_LABEL and the
// Platform cell renders the raw literal instead of "macOS".
const OS_ENUM_TO_PLATFORM = {
    Windows: "PLATFORM_WINDOWS",
    MacOs: "PLATFORM_MACOS",
    Linux: "PLATFORM_LINUX",
    Unknown: "PLATFORM_UNKNOWN",
};

// Position readout for the inventory table — which rows of `rowCount` the reader
// can currently see — or null when no range should be shown. `band` is
// DataTable's `onVisibleRange` payload: `{ start, end, measured }`, half-open,
// zero-based.
//
// Three distinct reasons to show nothing, which must NOT collapse into one:
//   - a search is filtering: the match count, not a scroll position, is the
//     useful number;
//   - the band is unmeasured: the virtualizer reports [0, count) both when every
//     row genuinely fits AND when it could not measure the viewport, so an
//     unmeasured band is not evidence that the list fits;
//   - the reader can see the whole list at once: a range spanning everything is
//     noise next to the plain total.
export function inventoryRangeLabel(band, rowCount, isFiltering) {
    if (isFiltering || !band?.measured) return null;
    const n = Number.isFinite(rowCount) ? rowCount : 0;
    if (n <= 0) return null;
    const start = Math.min(band.start + 1, n);
    const end = Math.min(band.end, n);
    if (start <= 1 && end >= n) return null;
    return { start, end, total: n };
}

function installedToolsFromGraphRow(row) {
    const keys = ["names", "providers", "types", "versions", "paths"];
    const arrays = keys.map((key) => row[`installed_agent_${key}`] ?? []);
    if (arrays.some((array) => !Array.isArray(array) || array.length !== arrays[0].length)) {
        throw new Error("Installed tool fields have inconsistent entry counts.");
    }
    return arrays[0].map((_, i) => ({
        tool_name: arrays[0][i],
        tool_provider: arrays[1][i],
        tool_type: arrays[2][i],
        version: arrays[3][i],
        path: arrays[4][i],
    }));
}

// Reshape a flat analytics-graph row (the endpoint-inventory listing served via
// the graph — see apiClient `executeInventoryRows`) into the SAME row object
// `flattenInventoryRow` produces from the legacy REST `EndpointMetadataInfo`, so
// the table renderer and detail drawer are identical on both paths.
//
// The graph returns OS scalars and aligned installed-tool arrays. Reuse the
// display derivation by rebuilding its input object without JSON serialization.
export function flattenInventoryGraphRow(r) {
    const row = r ?? {};
    return flattenInventoryRow({
        endpointId: row.id ?? "",
        metadata: {
            hostInfo: {
                hostname: row.hostname ?? "",
                serialNumber: row.serial ?? "",
                registeredUser: row.registered_user ?? "",
                deviceFingerprintV1Sha256: row.device_fingerprint_v1_sha256 ?? "",
            },
            hardwareInfo: {
                manufacturer: row.manufacturer ?? "",
                model: row.model ?? "",
                processorModel: row.processor_model ?? "",
                coreCount: row.core_count ?? null,
                systemMemoryMb: row.system_memory_mb ?? null,
                primaryDriveDiskSizeGb: row.drive_disk ?? null,
                primaryDriveFreeSpaceGb: row.drive_free ?? null,
            },
            osVersion: { version: {
                major: row.os_major,
                product_name: row.os_product_name,
                minor: row.os_minor,
                codename: row.os_codename,
                edition: row.os_edition,
                display_version: row.os_display_version,
            } },
            installedAiAgents: installedToolsFromGraphRow(row),
            platform: OS_ENUM_TO_PLATFORM[row.operating_system] ?? row.operating_system ?? "",
            agentVersion: row.agent_version ?? "",
            watchdogVersion: row.watchdog_version ?? "",
            agentExecutableSha256: row.agent_sha ?? "",
            watchdogExecutableSha256: row.watchdog_sha ?? "",
            snapshotTime: row.last_updated ?? null,
        },
    });
}

// Add the Postgres-authoritative allocation provenance to an analytics row.
// Keep the ClickHouse digest when present: it is the inventory data path and
// may be newer than the bounded identity lookup. The identity copy is only a
// fallback for rows whose metadata predates digest reporting.
export function mergeInventoryIdentity(row, identityInfo) {
    if (!identityInfo) return row;

    const forkedFromEndpointIdRaw =
        identityInfo.forkedFromEndpointId ??
        identityInfo.forked_from_endpoint_id ??
        row.forkedFromEndpointIdRaw ??
        "";

    return {
        ...row,
        deviceFingerprintV1Sha256:
            row.deviceFingerprintV1Sha256 ||
            identityInfo.deviceFingerprintV1Sha256 ||
            identityInfo.device_fingerprint_v1_sha256 ||
            "",
        identityCreationReason:
            identityInfo.creationReason ??
            identityInfo.creation_reason ??
            row.identityCreationReason ??
            "",
        forkedFromEndpointIdRaw,
        forkedFromEndpointId: endpointIdToUuid(forkedFromEndpointIdRaw),
    };
}

// A tool's dedup identity. A specific `tool_name` uniquely identifies the tool,
// so all of its install paths and versions collapse into one row (this is what
// folds a version-explosion of VS Code extension dirs, or a machine-wide tool
// re-scanned once per user profile, into a single entry). When only a coarse
// provider/tool_type tag is available — older agents that predate `tool_name`,
// where e.g. every local runtime reports provider "local" — that display name
// is shared by DISTINCT tools, so keying on it alone would merge them and hide
// an installed tool. Fall back to the install path there: same-path repeats
// still collapse, but two different tools stay two rows.
//
// Residual, self-healing gap: the path fallback keys on the RAW path string,
// not a canonicalized one — the dashboard only has the literal path from the
// snapshot and can't resolve symlinks on the endpoint. So an OLD agent (one
// that predates both `tool_name` and the agent-side path canonicalization)
// that reports the same physical tool under two different literal paths (a
// `~/.local/bin` symlink vs its Homebrew target) still shows two rows. Those
// endpoints self-heal on upgrade: the newer agent emits `tool_name` (so the
// specific-name branch collapses them) and canonicalizes paths at the source.
// Exercised by the "distinct real paths" test.
function agentDedupKey(a, displayName) {
    const specific = a.toolName ?? a.tool_name;
    if (specific) return `name\u0000${specific}`;
    return `path\u0000${displayName}\u0000${a.path ?? a.toolPath ?? ""}`;
}

// One entry per tool (see `agentDedupKey`); nameless reports are dropped. Each
// entry carries `version` (the highest, for the at-a-glance row) and `versions`
// (every distinct non-empty version seen, highest first) so the renderer can
// flag a multi-version install with a `*` and list them all on hover. The
// result is sorted alphabetically by the DISPLAYED label — the agent reports
// tools in its own scan order (a fixed per-detector sequence, not alphabetical),
// which reads as arbitrary in the UI. Shared by the structured list and the
// flat search/tooltip string so both stay consistent.
function dedupeAiAgents(installed) {
    if (!Array.isArray(installed)) return [];
    const byKey = new Map();
    const order = [];
    for (const a of installed) {
        const name = agentDisplayName(a);
        if (!name) continue;
        const version = a.version ?? "";
        const key = agentDedupKey(a, name);
        let entry = byKey.get(key);
        if (!entry) {
            entry = { name, versions: [], path: a.path ?? a.toolPath ?? "" };
            byKey.set(key, entry);
            order.push(key);
        }
        entry.name = name;
        if (version && !entry.versions.includes(version)) entry.versions.push(version);
    }
    const entries = order.map((key) => {
        const entry = byKey.get(key);
        const versions = entry.versions.slice().sort((a, b) => compareVersions(b, a));
        return { name: entry.name, version: versions[0] ?? "", versions, path: entry.path };
    });
    // Entries are grouped by install identity but PRESENTED by label, and the two
    // are derived from different fields. Two installs with distinct paths and no
    // `tool_name` (an older agent, or two different local runtimes both tagged
    // "local") are correctly kept apart, yet render as the same string, so an
    // endpoint can show three lines all reading "Unspecified" with nothing to tell
    // them apart. Collapsing them would hide an installed tool, which the "does not
    // merge distinct tools that share a coarse fallback name" test guards against.
    // So keep the grouping and surface the path that already distinguishes them,
    // only where the label is genuinely ambiguous within this endpoint's list.
    const labelCounts = new Map();
    for (const e of entries) {
        const label = agentDisplayLabel(e.name);
        labelCounts.set(label, (labelCounts.get(label) ?? 0) + 1);
    }
    return entries
        .map((e) => {
            const out = { name: e.name, version: e.version, versions: e.versions };
            if (e.path && labelCounts.get(agentDisplayLabel(e.name)) > 1) {
                out.distinguisher = e.path;
            }
            return out;
        })
        .sort((a, b) =>
            agentDisplayLabel(a.name).localeCompare(agentDisplayLabel(b.name), undefined, {
                sensitivity: "base",
                numeric: true,
            }),
        );
}

// ── Filter / sort (shared by the admin page and the Inventory lens) ──

export function filterInventoryRows(rows, query) {
    const q = String(query || "").trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
        for (const f of INVENTORY_SEARCHABLE_FIELDS) {
            const v = r[f];
            if (typeof v === "string" && v.toLowerCase().includes(q)) return true;
        }
        return false;
    });
}

// Dates and numbers compare numerically; everything else is a
// locale/numeric-aware string compare. Empty values sort last
// regardless of direction so "—" cells don't crowd the top.
export function sortInventoryRows(rows, sortBy) {
    if (!sortBy) return rows;
    const { id, dir } = sortBy;
    const mul = dir === "asc" ? 1 : -1;
    const copy = rows.slice();
    copy.sort((a, b) => {
        const av = a[id];
        const bv = b[id];
        const aEmpty = av == null || av === "";
        const bEmpty = bv == null || bv === "";
        if (aEmpty && bEmpty) return 0;
        if (aEmpty) return 1;
        if (bEmpty) return -1;
        if (av instanceof Date && bv instanceof Date) return (av.getTime() - bv.getTime()) * mul;
        if (typeof av === "number" && typeof bv === "number") return (av - bv) * mul;
        return String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: "base" }) * mul;
    });
    return copy;
}
